import debounce from 'lodash.debounce';
import type { IntlShape } from 'react-intl';
import * as Yup from 'yup';

import Provision from '../../../api/provision';
import { regexValidator } from '../../../helpers/regexValidator';

interface ValidationResponse {
  data?: {
    available: boolean;
  };
}

const validateNameAvailability = async (
  value: string,
  cloudName: string,
  juiceGroup: string
): Promise<boolean> => {
  try {
    const response: ValidationResponse = await Provision.validateOsProjectName(
      value,
      cloudName,
      juiceGroup
    );
    return response.data?.available === true;
  } catch (error) {
    console.error('Error validating name availability:', error);
    return false;
  }
};

const debouncedValidation = debounce(
  (value: string, cloudName: string, juiceGroup: string, resolve: (value: boolean) => void) => {
    validateNameAvailability(value, cloudName, juiceGroup).then(resolve);
  },
  500
);

const checkDuplicateName = (value: string, context: Yup.TestContext): Promise<boolean> => {
  const { cloudName, juiceGroup } = context.parent as { cloudName: string; juiceGroup: string };

  if (!value || !cloudName || !juiceGroup) {
    return Promise.resolve(true);
  }

  return new Promise(resolve => {
    debouncedValidation(value, cloudName, juiceGroup, resolve);
  });
};

export const getValidationSchema = (intl: IntlShape): Yup.ObjectSchema<Record<string, unknown>> => {
  const required = intl.formatMessage({ id: 'formRequiredError' });
  const exceedsQuota = intl.formatMessage({ id: 'exceedsAvailableQuota' });

  return Yup.object().shape({
    osProjectName: Yup.string()
      .required(required)
      .matches(regexValidator.noSpace, intl.formatMessage({ id: 'spacesNotAllowed' }))
      .test({
        name: 'check-duplicate-name',
        message: 'osProjectNameDuplicate',
        test: checkDuplicateName,
        exclusive: true,
      }),
    projectId: Yup.string().required(required),
    productId: Yup.string().required(required),
    cloudName: Yup.string().required(required),
    cpuCores: Yup.number()
      .required(required)
      .min(1, intl.formatMessage({ id: 'mustBeGreaterThanZero' }))
      .max(Yup.ref('$unusedQuota.compute.cores'), exceedsQuota),
    ram: Yup.number()
      .required(required)
      .min(1, intl.formatMessage({ id: 'mustBeGreaterThanZero' }))
      .max(Yup.ref('$unusedQuota.compute.ram'), exceedsQuota),
    diskQuota: Yup.number()
      .required(required)
      .min(1, intl.formatMessage({ id: 'mustBeGreaterThanZero' }))
      .max(Yup.ref('$unusedQuota.volume.gigabytes'), exceedsQuota),
    floatingIps: Yup.number()
      .required(required)
      .min(0, intl.formatMessage({ id: 'mustBeZeroOrGreater' }))
      .max(Yup.ref('$unusedQuota.network.floatingIps'), exceedsQuota),
  });
};
